import { Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';

import { ProjectStructureResponse } from '../../models/project-structure.model';
import { ProjectStructureEntitiesResponse } from '../../models/project-structure-entities.model';
import { ApiService } from '../api/api.service';
import { normalizeLanguageTag } from '../../utils/language';

@Injectable({
  providedIn: 'root'
})
export class StructureService {
  private readonly structureCache = new Map<string, Observable<ProjectStructureResponse>>();
  private readonly entitiesCache = new Map<string, Observable<ProjectStructureEntitiesResponse>>();

  constructor(private readonly apiService: ApiService) {}

  getProjectStructure(projectId: string): Observable<ProjectStructureResponse> {
    const normalizedProjectId = projectId.trim();
    if (!this.structureCache.has(normalizedProjectId)) {
      const structure$ = this.apiService.getProjectStructure(normalizedProjectId).pipe(shareReplay(1));
      this.structureCache.set(normalizedProjectId, structure$);
    }

    return this.structureCache.get(normalizedProjectId)!;
  }

  clearCache(projectId?: string): void {
    if (projectId) {
      this.structureCache.delete(projectId.trim());
      for (const key of [...this.entitiesCache.keys()]) {
        if (key.startsWith(`${projectId.trim()}|`)) {
          this.entitiesCache.delete(key);
        }
      }
      return;
    }

    this.structureCache.clear();
    this.entitiesCache.clear();
  }

  clearEntitiesCache(projectId?: string): void {
    if (projectId) {
      const normalizedProjectId = projectId.trim();
      for (const key of [...this.entitiesCache.keys()]) {
        if (key.startsWith(`${normalizedProjectId}|`)) {
          this.entitiesCache.delete(key);
        }
      }
      return;
    }

    this.entitiesCache.clear();
  }

  getProjectStructureEntities(projectId: string, contentTypeId: string, language: string): Observable<ProjectStructureEntitiesResponse> {
    const normalizedProjectId = projectId.trim();
    const normalizedType = contentTypeId.trim();
    const normalizedLang = normalizeLanguageTag(language, 'es');
    const cacheKey = `${normalizedProjectId}|${normalizedLang}|${normalizedType}`;

    if (!this.entitiesCache.has(cacheKey)) {
      const entities$ = this.apiService
        .getProjectStructureEntities({
          projectId: normalizedProjectId,
          contentTypeId: normalizedType,
          language: normalizedLang
        })
        .pipe(shareReplay(1));
      this.entitiesCache.set(cacheKey, entities$);
    }

    return this.entitiesCache.get(cacheKey)!;
  }
}
